import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/Button'
import { Dialog } from '@/components/ui/Dialog'
import { ModalShell } from '@/components/ui/ModalShell'
import { ScrollableList } from '@/components/ui/ScrollableList'
import { TrashIcon } from '@/components/ui/icons'
import { useModalStore } from '@/hooks/useModalStore'
import { useDiagramStore } from '@/stores'
import { genId } from '@/utils/id'

interface DraftGroup {
  id: string
  name: string
  color: string
  isNew?: boolean
}

function slugifyGroupName(name: string) {
  return name
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_|_$/g, '')
    .slice(0, 20) || genId('grp')
}

export function GroupsModal() {
  const { t } = useTranslation()
  const open = useModalStore((state) => state.groupsOpen)
  const close = useModalStore((state) => state.closeGroups)
  const groups = useDiagramStore((state) => state.groups)
  const addGroup = useDiagramStore((state) => state.addGroup)
  const updateGroup = useDiagramStore((state) => state.updateGroup)
  const deleteGroup = useDiagramStore((state) => state.deleteGroup)

  const newNameInputRef = useRef<HTMLInputElement | null>(null)
  const [drafts, setDrafts] = useState<DraftGroup[]>([])
  const [newName, setNewName] = useState('')
  const [newColor, setNewColor] = useState('#4F46E5')
  const [errorMessage, setErrorMessage] = useState('')
  const [pendingDeleteId, setPendingDeleteId] = useState<string | null>(null)

  useEffect(() => {
    if (!open) return
    setDrafts(Object.values(groups).map((group) => ({ id: group.id, name: group.name, color: group.color })))
    setNewName('')
    setNewColor('#4F46E5')
    setErrorMessage('')
    setPendingDeleteId(null)
    requestAnimationFrame(() => {
      newNameInputRef.current?.focus()
    })
  }, [groups, open])

  const pendingDelete = pendingDeleteId ? drafts.find((group) => group.id === pendingDeleteId) ?? null : null
  const deleteQuestion = pendingDelete
    ? t('groups.removeQuestion', { name: pendingDelete.name })
    : ''

  const addDraftGroup = () => {
    const normalizedName = newName.trim()
    if (!normalizedName) {
      setErrorMessage(t('groups.nameRequired'))
      return
    }

    const candidateId = slugifyGroupName(normalizedName)
    let finalId = candidateId
    while (drafts.some((group) => group.id === finalId)) {
      finalId = `${candidateId}_${Math.random().toString(36).slice(2, 5)}`
    }

    setDrafts((current) => [...current, { id: finalId, name: normalizedName, color: newColor, isNew: true }])
    setNewName('')
    setNewColor('#4F46E5')
    setErrorMessage('')
  }

  const save = () => {
    const invalid = drafts.find((group) => !group.name.trim())
    if (invalid) {
      setErrorMessage(t('groups.nameRequired'))
      return
    }

    const existingIds = new Set(Object.keys(groups))
    const draftIds = new Set(drafts.map((group) => group.id))

    Object.keys(groups)
      .filter((groupId) => !draftIds.has(groupId))
      .forEach((groupId) => deleteGroup(groupId))

    drafts.forEach((group) => {
      if (existingIds.has(group.id)) {
        updateGroup(group.id, { name: group.name.trim(), color: group.color })
      } else {
        addGroup({ id: group.id, name: group.name.trim(), color: group.color })
      }
    })

    close()
  }

  return (
    <>
      <ModalShell
        open={open}
        onClose={close}
        title={t('groups.title')}
        description={t('groups.description')}
        widthClassName="max-w-4xl"
        footer={(
          <>
            <Button variant="ghost" onClick={close}>{t('common.cancel')}</Button>
            <Button variant="primary" onClick={save}>{t('groups.save')}</Button>
          </>
        )}
      >
        <div className="space-y-4">
          <div className="rounded-xl border px-4 py-4" style={{ borderColor: 'var(--border)', background: 'var(--bg-card)' }}>
            <div className="grid gap-3 lg:grid-cols-[48px_minmax(0,1fr)_120px]">
              <label className="relative flex h-10 w-12 cursor-pointer items-center justify-center rounded-xl" style={{ background: 'transparent' }}>
                <span className="h-8 w-8 rounded-full" style={{ background: newColor }} />
                <input
                  type="color"
                  value={newColor}
                  onChange={(event) => setNewColor(event.target.value)}
                  className="absolute inset-0 cursor-pointer opacity-0"
                  aria-label={t('tableModal.groupColor')}
                />
              </label>
              <input
                ref={newNameInputRef}
                type="text"
                value={newName}
                onChange={(event) => setNewName(event.target.value)}
                onKeyDown={(event) => { if (event.key === 'Enter') addDraftGroup() }}
                placeholder={t('groups.namePlaceholder')}
                className="morph-input"
              />
              <Button variant="primary" onClick={addDraftGroup}>{t('groups.add')}</Button>
            </div>
            {errorMessage ? (
              <div className="mt-3 rounded-lg border px-3 py-2 text-[11px]" style={{ borderColor: 'var(--color-danger-border)', background: 'var(--color-danger-bg)', color: 'var(--color-danger)' }}>
                {errorMessage}
              </div>
            ) : null}
          </div>

          <ScrollableList maxVisibleItems={6} itemHeight={64} className="space-y-2 pr-1">
            {drafts.length ? drafts.map((group) => (
              <div key={group.id} className="rounded-xl border px-3 py-3" style={{ borderColor: 'var(--border)', background: 'var(--bg-card)' }}>
                <div className="grid items-center gap-3 lg:grid-cols-[48px_minmax(0,1fr)_auto]">
                  <label className="relative flex h-10 w-12 cursor-pointer items-center justify-center rounded-xl" style={{ background: 'transparent' }}>
                    <span className="h-8 w-8 rounded-full" style={{ background: group.color }} />
                    <input
                      type="color"
                      value={group.color}
                      onChange={(event) => setDrafts((current) => current.map((entry) => entry.id === group.id ? { ...entry, color: event.target.value } : entry))}
                      className="absolute inset-0 cursor-pointer opacity-0"
                      aria-label={`${t('tableModal.groupColor')} ${group.name}`}
                    />
                  </label>

                  <div>
                    <input
                      type="text"
                      value={group.name}
                      onChange={(event) => setDrafts((current) => current.map((entry) => entry.id == group.id ? { ...entry, name: event.target.value } : entry))}
                      className="morph-input"
                    />
                  </div>

                  <button
                    type="button"
                    onClick={() => setPendingDeleteId(group.id)}
                    className="inline-flex h-9 w-9 items-center justify-center rounded-full transition-colors"
                    style={{ color: 'var(--color-danger)', background: 'var(--color-danger-bg)' }}
                    title={t('common.delete')}
                    aria-label={t('common.delete')}
                  >
                    <TrashIcon />
                  </button>
                </div>
              </div>
            )) : (
              <div className="rounded-xl border border-dashed px-4 py-8 text-center text-[11px]" style={{ borderColor: 'var(--border)', color: 'var(--text-dim)' }}>
                {t('sidebar.noGroup')}
              </div>
            )}
          </ScrollableList>
        </div>
      </ModalShell>

      <Dialog
        open={!!pendingDelete}
        title={t('groups.removeTitle')}
        onClose={() => setPendingDeleteId(null)}
        onConfirm={() => {
          if (!pendingDelete) return
          setDrafts((current) => current.filter((group) => group.id != pendingDelete.id))
          setPendingDeleteId(null)
        }}
        footer={(
          <>
            <Button variant="ghost" onClick={() => setPendingDeleteId(null)}>{t('common.cancel')}</Button>
            <Button
              variant="danger"
              onClick={() => {
                if (!pendingDelete) return
                setDrafts((current) => current.filter((group) => group.id != pendingDelete.id))
                setPendingDeleteId(null)
              }}
            >
              {t('common.delete')}
            </Button>
          </>
        )}
      >
        {deleteQuestion}
      </Dialog>
    </>
  )
}
